import { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Page,
  Card,
  InlineGrid,
  Text,
  BlockStack,
  Button,
  InlineStack,
  Spinner,
  Tabs,
  Badge,
  Link,
  EmptyState,
  TabProps,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useNavigate, useLocation } from "react-router-dom";
import CommentsFeature from "components/comment/comments";
import { useSelector, useDispatch } from "react-redux";
import { fetchOrderDetails, setDestroy } from "./order-container.slice";
import { fetchCompanyDetails } from "containers/settings/settings-container.slice";
import OrderViewSkeleton from "./order-view-skeleton";
import OrderContractHint from "features/order-contract-hint/order-contract-hint";
import { CommentTypes } from "types/Comment";
import DataTable from "components/datatable/Datatable";
import { AppDispatch, RootState } from "configureStore";
import PrettyDate from "components/format/PrettyDate";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import Emails from "components/emails/Emails";
import { formatPrettyDate } from "utils/helperFunctions";
import { extractSignersFromList, getSignerId } from "utils/signatureUtils";
import "./styles.scss";
import ReplaceFile from "features/replace-file/replace-file";
import { API_URL } from "constants.js";
import Files from "features/files/files";
import { FulfillmentSection } from "features/fulfillment";
import {
  openDokobitSignUrl,
  createHandleRepeatEmail,
} from "utils/signatureViewUtils";
import { normalizeBuildPdfTemplateId } from "components/customer/build-pdf/build-pdf-utils";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import { setOrderClosed } from "services/orders.service";
import { toast } from "sonner";
import { formatPeriodMonth } from "features/billing-period/billing-period";
import { Badge as TwBadge } from "catalyst-ui-kit/badge";
import { vilniusYmd } from "utils/vilnius-date";
import ExternalDocumentBox from "./external-document-box";
import OrderInvoicesTable, { InvoiceStatusBadge } from "./order-invoices-table";
import { OrderTab, orderTabFromUrlSegment } from "./order-tabs";
import Currency from "components/format/currency/currency";
import { btnLinkCls, btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";
import useIsPhone from "features/files/use-is-phone";
import { formatInvoicePublicNumber } from "utils/invoice-number";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { contractBlockReason } from "utils/orderContractGate";
import ContractDeferredSwitch from "./contract-deferred-switch";
import OrderStatusStrip, {
  contractStateOf,
  ORDER_STEPS_ID,
} from "./order-status-strip";

type AmendmentSlotSettings = {
  orderAmendmentTemplateSource?: string;
  orderAmendmentTemplate?: string | { id?: string };
  orderAmendmentSeries?: string;
  orderAmendmentFileType?: string;
  orderAmendmentCategory?: string;
} | null;

/**
 * Whether the company's order amendment slot is configured: a custom template,
 * or the system template with its series, file type and category.
 *
 * A custom template numbers like any other generated document — it needs a
 * series of its own (`Templates.series`), not just to be selected. The
 * company entity only ever carries the template's id (see
 * `CompanyTemplateConfigService.hydrateCompanyWithOrderAmendmentConfig`), so
 * `customTemplateHasSeries` is resolved separately, by fetching that template
 * (see the effect below) — this mirrors `files/build-pdf`, which resolves the
 * series from the Templates row itself for a custom slot, not from the
 * company/slot config (`services/orders/external-contract.js`'s
 * `amendmentSlotRefusal`). `null` (not yet resolved, or nothing to resolve)
 * reads as "not ready" — never optimistic while we don't actually know.
 */
function hasCompanyAmendmentSlot(
  companyEntity: AmendmentSlotSettings,
  customTemplateHasSeries: boolean | null
): boolean {
  if (!companyEntity) return false;
  const src = companyEntity.orderAmendmentTemplateSource || "system";
  if (src === "custom") {
    return !!companyEntity.orderAmendmentTemplate && customTemplateHasSeries === true;
  }
  return !!(
    companyEntity.orderAmendmentSeries &&
    companyEntity.orderAmendmentFileType &&
    companyEntity.orderAmendmentCategory
  );
}

function getOrderAmendmentModalProps(
  companyEntity: {
    orderAmendmentTemplateSource?: string;
    orderAmendmentTemplate?: string | { id?: string };
    orderAmendmentSeries?: string;
    orderAmendmentFileType?: string;
    orderAmendmentCategory?: string;
  } | null,
  amendmentParentTemplateId: string | { id?: string }
): { template: string; orderAmendmentFileType?: string } {
  if (!companyEntity) {
    return { template: normalizeBuildPdfTemplateId(amendmentParentTemplateId) };
  }
  const src = companyEntity.orderAmendmentTemplateSource || "system";
  if (src === "custom" && companyEntity.orderAmendmentTemplate) {
    return {
      template: normalizeBuildPdfTemplateId(companyEntity.orderAmendmentTemplate),
    };
  }
  if (
    src === "system" &&
    companyEntity.orderAmendmentSeries &&
    companyEntity.orderAmendmentFileType &&
    companyEntity.orderAmendmentCategory
  ) {
    return { template: "", orderAmendmentFileType: "order_amendment" };
  }
  return { template: normalizeBuildPdfTemplateId(amendmentParentTemplateId) };
}

function InvoiceHints({ hints }: { hints: string[] }) {
  return (
    <div className="space-y-1">
      {hints.map((hint) => (
        <p key={hint} className="text-sm text-zinc-500">
          {hint}
        </p>
      ))}
    </div>
  );
}

function OrderContainer() {
  const { t } = useTranslation();
  const { companyId, customerId, orderId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { session, role } = useSelector(
    (state: RootState) => state.authReducer
  );

  const hasUserSignaturePermission =
    role?.permissionsgroups?.includes("Signature");

  const isUserMaster = role?.permissionsgroups?.includes("Master");

  const { orderEntity, isLoading, isError, requestedOrderId } = useSelector(
    (state: RootState) => state.orderContainerReducer
  );

  const { companyEntity: companySettingsEntity } = useSelector(
    (state: RootState) => state.companyContainerReducer
  );

  useEffect(() => {
    if (companyId) {
      dispatch(fetchCompanyDetails({ companyId }));
    }
  }, [companyId, dispatch]);

  // Whether the company's custom amendment template (if any) has its own
  // series — see hasCompanyAmendmentSlot above. null while unresolved or not
  // applicable (system source, no template, order not external).
  const [customAmendmentTemplateHasSeries, setCustomAmendmentTemplateHasSeries] =
    useState<boolean | null>(null);
  const isExternalOrderEntity = orderEntity?.origin === "external";
  const amendmentTemplateSource =
    companySettingsEntity?.orderAmendmentTemplateSource || "system";
  // Always a plain id (CompanyTemplateConfigService never populates it).
  const amendmentTemplateId = companySettingsEntity?.orderAmendmentTemplate;

  useEffect(() => {
    if (
      !isExternalOrderEntity ||
      !companyId ||
      amendmentTemplateSource !== "custom" ||
      !amendmentTemplateId
    ) {
      setCustomAmendmentTemplateHasSeries(null);
      return;
    }
    let cancelled = false;
    fetchFileTemplateById(companyId, amendmentTemplateId)
      .then((templateEntity: any) => {
        if (!cancelled) setCustomAmendmentTemplateHasSeries(!!templateEntity?.series);
      })
      .catch(() => {
        if (!cancelled) setCustomAmendmentTemplateHasSeries(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isExternalOrderEntity, companyId, amendmentTemplateSource, amendmentTemplateId]);

  const loadOrderData = useCallback(() => {
    if (companyId && orderId) {
      dispatch(fetchOrderDetails({ companyId, orderId }));
    }
  }, [dispatch, companyId, orderId]);

  useEffect(() => {
    loadOrderData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadOrderData]);

  // The URL names the open tab; clicking a tab rewrites the URL.
  const tabInUrl = orderTabFromUrlSegment(location.pathname.split("/").pop());
  const [closingOrder, setClosingOrder] = useState(false);

  const handleTabChange = (tab: OrderTab) => {
    if (customerId) {
      navigate(
        `/app/${companyId}/customers/${customerId}/orders/${orderId}/${tab}`,
        {
          replace: true,
        }
      );
    } else {
      navigate(`/app/${companyId}/orders/${orderId}/${tab}`, {
        replace: true,
      });
    }
  };

  // The loaded order matches the route only when its id equals orderId — guards
  // against briefly showing the previous order's data after navigating to a
  // different one (the route reconciles instead of remounting).
  const isOrderReady = !!orderEntity && orderEntity.id === orderId;

  const ordersBackUrl = customerId
    ? `/app/${companyId}/customers/${customerId}/orders`
    : `/app/${companyId}/orders`;

  // If loading the requested order failed, show an error with a retry path
  // instead of an endless skeleton. requestedOrderId scopes the error to the
  // order that actually failed (the slice clears it when a new fetch starts),
  // so a stale error never blocks a different order, and a failed background
  // refetch (when valid data is already shown) keeps the page visible.
  if (isError && requestedOrderId === orderId && !isOrderReady) {
    return (
      <Page
        fullWidth
        backAction={{ content: t("orders"), url: ordersBackUrl }}
        title={t("orders")}
      >
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="critical">
              {t("not_fetch", { defaultValue: "Could not load order" })}
            </Text>
            <div>
              <Button
                onClick={() => {
                  if (companyId && orderId) {
                    dispatch(fetchOrderDetails({ companyId, orderId }));
                  }
                }}
              >
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </div>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (!isOrderReady) {
    return <OrderViewSkeleton selected={tabInUrl} />;
  }

  // Whether the order recurs is known only once it has loaded. Such an order is
  // mostly about its monthly invoices, so with no tab in the URL it opens there.
  const selected: OrderTab =
    tabInUrl ?? (orderEntity.isRecurring ? "invoices" : "fulfillment");

  // Calculate fulfillment badge (reservations + deliveries count)
  const fulfillmentCount = (orderEntity?.reservations?.length || 0) + (orderEntity?.deliveries?.length || 0);

  const invoiceCount = orderEntity.invoices?.length ?? 0;

  const tabs: (TabProps & { id: OrderTab })[] = [
    {
      id: "invoices",
      content: t("invoices"),
      accessibilityLabel: t("invoices"),
      panelID: "invoices",
      badge: invoiceCount.toString(),
    },
    {
      id: "products",
      content: t("products"),
      accessibilityLabel: t("products"),
      panelID: "products",
    },
    {
      id: "fulfillment",
      content: t("fulfillment"),
      accessibilityLabel: t("fulfillment"),
      panelID: "fulfillment",
      badge: fulfillmentCount > 0 ? fulfillmentCount.toString() : undefined,
    },
    {
      id: "amendments",
      content: t("amendments"),
      accessibilityLabel: t("amendments"),
      panelID: "amendments",
      badge: orderEntity?.amendmentFiles?.length ? orderEntity.amendmentFiles.length?.toString() : "0",
    },
    {
      id: "emails",
      content: t("emails"),
      panelID: "emails",
      badge: orderEntity?.emails?.length
        ? orderEntity.emails.length?.toString()
        : "0",
    }
  ];

  /**
   * An external contract was signed outside the system: it carries its own
   * number, and its signed PDF is attached instead of generated and signed here.
   */
  const isExternalOrder = orderEntity.origin === "external";

  // An external contract is known by its own number, not the internal counter.
  const orderTitle = `[${
    (isExternalOrder ? orderEntity.externalNumber : orderEntity.number) || ""
  }] ${formatPrettyDate(orderEntity.date)} ${orderEntity.customer.name}`;

  const handleInvoiceSettingsClick = () => {
    dispatch(
      setModalxOptions({
        title: t("invoice_settings", { defaultValue: "Invoice settings" }),
      })
    );
    dispatch(setModalxComponent("InvoiceSettingsForm"));
    dispatch(setModalxComponentProps({ id: orderId }));
  };

  const handleProductEditClick = () => {
    dispatch(setModalxComponent("OrdersProductForm"));
    dispatch(setModalxComponentProps({ id: orderId }));
  };

  const handleResetSignaturesClick = () => {
    if (!id) return;
    dispatch(setModalxComponent("ResetFileSignaturesConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
        isFullySigned: !!isSigned,
      })
    );
  };

  const handleDocumentClick = (
    customerId: string,
    templateIdForDoc: string | { id?: string },
    fileId: any,
    assignedUserId: any,
    orderEntity: any,
    isOrderAmendment?: boolean,
    orderAmendmentFileType?: string
  ) => {
    const normalizedTemplate = normalizeBuildPdfTemplateId(templateIdForDoc);
    dispatch(setModalxComponent("OrderDocumentForm"));
    dispatch(
      setModalxComponentProps({
        customerId,
        template: normalizedTemplate,
        fileId,
        assignedUserId,
        subject: "customer",
        orderEntity,
        ...(orderFinanceId && { financeId: orderFinanceId }),
        ...(isOrderAmendment === true && { isOrderAmendment: true }),
        ...(orderAmendmentFileType && { orderAmendmentFileType }),
        ...(isOrderAmendment === true &&
          normalizedTemplate && { lockedTemplateId: normalizedTemplate }),
      })
    );
  };

  /** The order's contract: its oldest file. */
  const mainFile = orderEntity.files?.[0];
  const isFileSigned = !!mainFile?.isSigned;

  const orderFinanceId =
    orderEntity?.customer?.finance != null
      ? typeof orderEntity.customer.finance === "string"
        ? orderEntity.customer.finance
        : orderEntity.customer.finance?.id
      : undefined;

  /** Preselected seller's contact person in the order's documents: the
   *  person responsible for the order, else the customer's assigned user. */
  const defaultContactUserId =
    orderEntity.responsibleUser?.id || orderEntity.customer?.assignedUser;

  const handleEditFile = (
    customerId: string,
    templateId: string,
    fileId: string,
    assignedUserId: string,
    id: string
  ) => {
    dispatch(setModalxComponent("OrderDocumentForm"));
    dispatch(
      setModalxComponentProps({
        customerId,
        template: templateId,
        fileId,
        assignedUserId,
        id,
        subject: "customer",
        orderEntity,
        ...(orderFinanceId && { financeId: orderFinanceId }),
      })
    );
  };

  const handleSendEmailWithAttachment = (
    fileId: string,
    customerId: string
  ) => {
    dispatch(setModalxComponent("ManualSignSendMailConfirm"));
    dispatch(setModalxComponentProps({ customerId, fileId, companyId }));
  };

  const handleUploadClick = async (
    id: string,
    contactSigners: string[],
    companyUserSigner: string,
    customerId: string,
    nontechuser: string,
    signType: string
  ) => {
    dispatch(setModalxComponent("SignRequestConfirmPopup"));
    dispatch(
      setModalxComponentProps({
        fileId: id,
        contactSigners,
        companyUserSigner,
        customerId,
        nontechuser,
        signType,
      })
    );
  };

  const FileSignUserAssignHandler = (
    fileId: string,
    relatedTo: string,
    permissionUserSigner: string,
    userSigner: string,
    customerId: string
  ) => {
    dispatch(setModalxComponent("FileSignUserAssignPopup"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        customerId,
        relatedTo,
        permissionUserSigner,
        userSigner,
      })
    );
  };

  const dokobitFileViewHandler = (companyId: string, fileId: string) =>
    openDokobitSignUrl(companyId, fileId);

  const handleRepeatEmail = createHandleRepeatEmail(companyId!, () => {
    if (orderId) dispatch(fetchOrderDetails({ companyId: companyId!, orderId }));
  });

  const {
    id,
    fileId,
    isSigned,
    relatedTo,
    signType,
    dokobit,
    signontab,
    signersList,
    templateId,
    signingStepsRequired,
    annulledAt,
  } = orderEntity?.files?.[0] || {};
  const hasSigners = !!(
    dokobit?.signersList?.length ||
    signontab?.signersList?.length ||
    signersList?.length
  );
  // An attached external contract has nothing here to reset — it was never
  // signed through the system, and the backend refuses the request outright.
  const showResetButton = !!(
    isUserMaster &&
    id &&
    !annulledAt &&
    !isExternalOrder &&
    (isSigned || hasSigners)
  );
  const amendmentMainFile = orderEntity?.files?.[0];
  const amendmentParentTemplateId =
    typeof (amendmentMainFile as { templateId?: unknown })?.templateId === "object" &&
    (amendmentMainFile as { templateId?: { id?: string } })?.templateId?.id
      ? (amendmentMainFile as { templateId: { id: string } }).templateId.id
      : (amendmentMainFile as { templateId?: string })?.templateId ?? "";

  const isAmendmentCreateDisabled =
    !!annulledAt ||
    ((orderEntity.amendmentFiles?.length ?? 0) > 0 && !orderEntity.amendmentFiles?.[0]?.isSigned);

  /**
   * An external contract's amendment cannot continue its parent's template or
   * numbering — the attached PDF has neither — nor depend on a master
   * agreement it never had. It needs the company's amendment slot.
   */
  const amendmentSlotReady = hasCompanyAmendmentSlot(
    companySettingsEntity,
    customAmendmentTemplateHasSeries
  );
  const canStartAmendment = isExternalOrder
    ? !!orderEntity?.files?.[0]?.isSigned && amendmentSlotReady
    : !!(
        orderEntity.customer?.masterAgreementFileId?.isSigned &&
        orderEntity?.files?.length > 0 &&
        orderEntity?.files[0]?.isSigned
      );

  const handleCreateAmendment = () => {
    const am = getOrderAmendmentModalProps(
      companySettingsEntity,
      isExternalOrder ? "" : amendmentParentTemplateId
    );
    handleDocumentClick(
      orderEntity.customer?.id,
      am.template,
      amendmentMainFile?.id,
      defaultContactUserId,
      orderEntity,
      true,
      am.orderAmendmentFileType
    );
  };

  const openInvoiceForm = (invoiceId?: string) => {
    dispatch(
      setModalxOptions({
        title: t("create_invoice")
      })
    );
    dispatch(setModalxComponent("InvoiceForm"));
    dispatch(
      setModalxComponentProps({
        orderId,
        customerId: orderEntity.customer?.id,
        invoiceId,
      })
    );
  };

  const handleGenerateInvoice = () => {
    openInvoiceForm();
  };

  /**
   * Close or reopen explicitly rather than toggling: a double click on a
   * toggle silently undoes itself, and closing stops the customer's recurring
   * invoicing. Reopening is immediate; closing goes through a confirmation
   * modal that owns the request. The server decides who may do either.
   */
  const handleCloseOrderClick = async () => {
    if (!companyId || !orderId) return;

    if (!orderEntity.isClosed) {
      dispatch(
        setModalxOptions({
          title: t("close_order", { defaultValue: "Close order" }),
          size: "small",
        })
      );
      dispatch(setModalxComponent("CloseOrderConfirm"));
      dispatch(
        setModalxComponentProps({
          companyId,
          orderId,
          isRecurring: !!orderEntity.isRecurring,
          arrearsPeriodFrom,
          onTerminal: !!companySettingsEntity?.isTerminalDeliveryActive,
          onDone: loadOrderData,
        })
      );
      return;
    }

    setClosingOrder(true);
    try {
      await setOrderClosed({ companyId, orderId, isClosed: false });
      toast.success(t("order_reopened", { defaultValue: "Order reopened" }));
      loadOrderData();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          t("error", { defaultValue: "Error" })
      );
    } finally {
      setClosingOrder(false);
    }
  };

  const hasProducts =
    (orderEntity.items?.length ?? 0) > 0 ||
    (orderEntity.productGroups?.length ?? 0) > 0;
  /**
   * For a recurring order the server works out whether a period can be billed
   * now, and which (`invoiceBilling`) — by period, not by the invoice date, and
   * with the contract end bounding the PERIOD. An order closed or past its end
   * can therefore still offer its final month, billed in arrears.
   */
  const billing = orderEntity.invoiceBilling || null;
  /**
   * A long-term order whose contract has nothing left to bill. Invoicing stops
   * by itself, without anyone closing the order — so it reads as its own
   * state, and extending the period in invoice settings resumes it.
   */
  const contractEnded =
    !!orderEntity.isRecurring &&
    !orderEntity.isClosed &&
    billing?.reason === "CONTRACT_ENDED";
  /** Null when signed (and not annulled), or a Master said the contract comes later. */
  const contractBlock = contractBlockReason(orderEntity, mainFile);
  /**
   * The "contract will be signed later" switch matters only while there is no
   * signed contract; a non-Master sees it only when it is on.
   */
  const showContractDeferred =
    !isFileSigned && (!!isUserMaster || !!orderEntity.contractDeferred);
  /** An invoice would be due now, the contract aside. */
  const invoiceDueOtherwise =
    hasProducts &&
    (!orderEntity.isRecurring
      ? !orderEntity.isClosed && invoiceCount === 0
      : !!billing?.nextPeriod);
  const canCreateInvoice = invoiceDueOtherwise && !contractBlock;
  /** Say why the contract blocks whenever it is what stands in the way. */
  const explainContractBlock =
    hasProducts && (invoiceCount === 0 || invoiceDueOtherwise);

  // Why an invoice can or cannot be created now, and what happens next.
  const invoiceHints: string[] = [];
  if (!hasProducts && invoiceCount === 0) {
    invoiceHints.push(
      t("invoice_needs_products", {
        defaultValue: "Add products to the order before generating an invoice",
      })
    );
  }
  if (explainContractBlock && contractBlock === "annulled") {
    invoiceHints.push(
      t("invoice_err_order_contract_annulled", {
        defaultValue:
          "This order's contract has been annulled, so it can no longer be invoiced.",
      })
    );
  }
  if (explainContractBlock && contractBlock === "not_signed") {
    invoiceHints.push(
      isExternalOrder
        ? t("invoice_needs_attached_external_contract", {
            defaultValue:
              "Attach the signed contract PDF before creating an invoice",
          })
        : t("invoice_needs_signed_order", {
            defaultValue: "Order must be fully signed before creating an invoice",
          })
    );
  }
  if (orderEntity.isClosed && !billing?.nextPeriod) {
    invoiceHints.push(
      t("invoice_order_closed_hint", {
        defaultValue: "This order is closed — no new invoices will be created.",
      })
    );
  }
  if (orderEntity.isRecurring && billing?.nextPeriod && !contractBlock) {
    invoiceHints.push(
      t("invoice_next_period_hint", {
        month: formatPeriodMonth(billing.nextPeriod.periodFrom),
      })
    );
  }
  if (orderEntity.isRecurring && billing?.reason === "ALL_BILLED") {
    invoiceHints.push(t("invoice_all_periods_billed"));
  }
  if (orderEntity.isRecurring && billing?.reason === "NOT_YET") {
    invoiceHints.push(
      t("invoice_billing_not_started", {
        month: formatPeriodMonth(billing.billingStartDate),
      })
    );
  }
  if (contractEnded) {
    invoiceHints.push(
      t("invoice_contract_ended_hint", {
        defaultValue:
          "The contract period has ended — no new invoices will be created. Extend it in Invoice settings to resume, or close the order.",
      })
    );
  }

  // The server lists the order's invoices newest first.
  const lastInvoice = orderEntity.invoices?.[0];
  // A draft has no number yet.
  const lastInvoiceNumber = lastInvoice ? formatInvoicePublicNumber(lastInvoice) : "";

  /**
   * The month an arrears order will still bill after being closed today —
   * shown as a warning in the close dialog, because "closed" otherwise reads
   * as "nothing more will be invoiced". The server already knows which period
   * that is (`invoiceBilling.nextPeriod`), including billing start and the
   * previous-month schedule; re-deriving it from today's month drifts.
   * Null when nothing is pending.
   */
  const arrearsPeriodFrom =
    orderEntity.isRecurring && billing?.mode === "previous_month"
      ? billing.nextPeriod?.periodFrom ?? null
      : null;

  let customerContactSigned: any[] = [];
  let usersSignerList: any = [];

  if (dokobit) {
    customerContactSigned = dokobit.contactSigners || [];
    usersSignerList = dokobit.signersList || "";
  } else if (signontab) {
    customerContactSigned = signontab.contactSigners || [];
    usersSignerList = signontab.signersList || "";
  } else {
    customerContactSigned = [];
    usersSignerList = signersList?.map((signerEntity) => signerEntity.user) || [];
  }

  const downloadUrl = `${API_URL}/${companyId}/files/download/${id}`;
  const isDokobitDocument = signType === "Dokobit";
  const isSignontabDocument = signType === "Signontab";
  const canMakeNewRequest = dokobit ? dokobit.canMakeNewRequest : true;
  const {
    contactSigners,
    permissionUserSigner,
    userSigner,
  } = extractSignersFromList(signersList);

  // Annulment is terminal — even a Signature user must not be able to
  // regenerate (and thereby wipe the signatures of) an annulled document.
  const isOrderDocLocked =
    !!annulledAt || (isFileSigned && !hasUserSignaturePermission);

  // The reason sits under the button as text: a tooltip is never seen on touch.
  const lockedEditNode = (
    <BlockStack gap="100" inlineAlign="center">
      <Button disabled>{t("edit")}</Button>
      <Text as="p" tone="subdued" alignment="center">
        {t(
          annulledAt
            ? "document_annulled"
            : "document_is_signed_edit_is_not_allowed"
        )}
      </Text>
    </BlockStack>
  );

  const ProductEditNode = isOrderDocLocked ? (
    lockedEditNode
  ) : (
    <Button onClick={handleProductEditClick}>{t("edit")}</Button>
  );

  // Same rule as the disabled state of "edit" in the page header.
  const isEditDisabled = isFileSigned && !hasUserSignaturePermission;
  const editUrl = `${ordersBackUrl}/${orderId}/edit`;

  const TemplateEditNode = isOrderDocLocked ? (
    lockedEditNode
  ) : (
    <Button
      onClick={() => {
        handleEditFile(
          orderEntity.customer?.id,
          orderEntity.customer?.masterAgreementFileId?.templateId,
          orderEntity?.files[0]?.relatedTo,
          "",
          id
        );
      }}
    >
      {t("edit")}
    </Button>
  );

  return (
    <Page
      fullWidth
      backAction={{
        content: "order-container",
        url: customerId
          ? `/app/${companyId}/customers/${customerId}/orders`
          : `/app/${companyId}/orders`,
      }}
      title={orderTitle}
      titleMetadata={
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isLoading && <Spinner size="small" />}
          {isExternalOrder && (
            <TwBadge color="violet">
              {[
                t("order_external_badge"),
                orderEntity.externalNumber,
                vilniusYmd(orderEntity.date),
              ]
                .filter(Boolean)
                .join(" · ")}
            </TwBadge>
          )}
          {orderEntity.isRecurring && (
            <Badge tone="info">
              {t("order_recurring_badge", { defaultValue: "Monthly" })}
            </Badge>
          )}
          {orderEntity.isRecurring && (
            <span className="text-sm text-zinc-500">
              {orderEntity.contractEndDate
                ? t("contract_valid_until", {
                    date: vilniusYmd(orderEntity.contractEndDate),
                  })
                : t("contract_valid_indefinite")}
            </span>
          )}
          {orderEntity.isClosed && (
            <Badge tone="critical">
              {t("order_closed_badge", { defaultValue: "Closed" })}
            </Badge>
          )}
          {/* A lapsed contract stops invoicing on its own, which is a different
              thing from someone closing the order — show it as its own state. */}
          {contractEnded && (
            <Badge tone="warning">
              {t("order_ended_badge", { defaultValue: "Ended" })}
            </Badge>
          )}
          {annulledAt && <Badge tone="critical">{t("annulled_badge")}</Badge>}
          <AuditLogPopover
            companyId={companyId!}
            subjectType="Order"
            subjectId={orderId}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
      secondaryActions={[
        // The pencil in the customer's orders table is not drawn on a phone,
        // so editing is reachable from here. On a phone this header only has
        // a "..." menu, so "edit" is drawn as a button under the header
        // instead and the menu keeps just the risky "close order".
        ...(isPhone
          ? []
          : [{ content: t("edit"), url: editUrl, disabled: isEditDisabled }]),
        {
          content: orderEntity.isClosed
            ? t("reopen_order", { defaultValue: "Reopen order" })
            : t("close_order", { defaultValue: "Close order" }),
          onAction: handleCloseOrderClick,
          loading: closingOrder,
          destructive: !orderEntity.isClosed,
        },
      ]}
      primaryAction={{
        content: t("invoice_settings", { defaultValue: "Invoice settings" }),
        onAction: handleInvoiceSettingsClick,
        // Closing an order freezes how it bills; the server refuses the PATCH
        // too, so this only saves the user a pointless round trip.
        disabled: !!orderEntity.isClosed,
        helpText: orderEntity.isClosed
          ? t("invoice_settings_locked", {
              defaultValue: "Reopen the order to change its invoice settings",
            })
          : undefined,
      }}
    >
      {isPhone && (
        <div className={`mb-3 ${PHONE_PAGE_GUTTER}`}>
          {isEditDisabled ? (
            <>
              <button type="button" className={btnSecondaryCls} disabled>
                {t("edit")}
              </button>
              {/* A tooltip is never seen on touch, so the reason is plain text. */}
              <p className="mt-1 text-sm text-zinc-500">
                {t("document_is_signed_edit_is_not_allowed")}
              </p>
            </>
          ) : (
            <RouterLink to={editUrl} className={btnSecondaryCls}>
              {t("edit")}
            </RouterLink>
          )}
        </div>
      )}
      <OrderStatusStrip
        contract={contractStateOf(mainFile && { isSigned, annulledAt, hasSigners })}
        lastInvoice={lastInvoice}
        lastInvoiceNumber={lastInvoiceNumber}
      />
      <Layout>
        <Layout.Section>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Tabs
              tabs={tabs}
              selected={tabs.findIndex((tab) => tab.id === selected)}
              onSelect={(index) => handleTabChange(tabs[index].id)}
            />
            {/* <span style={{ marginRight: "10px" }}>{ProductEditNode}</span> */}
          </div>
          <BlockStack gap="500">
            {selected === "invoices" && (
              <Card roundedAbove="sm">
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <InvoiceHints hints={invoiceHints} />
                    {/* With no invoices yet the button stays, disabled, to show where
                        the first one comes from. */}
                    {(canCreateInvoice || invoiceCount === 0) && (
                      <button
                        type="button"
                        className={`${btnPrimaryCls} shrink-0`}
                        disabled={!canCreateInvoice}
                        onClick={handleGenerateInvoice}
                      >
                        {t("create_invoice")}
                      </button>
                    )}
                  </div>
                  {invoiceCount > 0 ? (
                    <OrderInvoicesTable
                      invoices={orderEntity.invoices!}
                      companyId={companyId}
                      onOpen={openInvoiceForm}
                    />
                  ) : (
                    <p className="text-sm text-zinc-500">
                      {t("invoices_none_yet", {
                        defaultValue: "No invoices yet",
                      })}
                    </p>
                  )}
                </div>
              </Card>
            )}

            {selected === "products" && (
              <>
                <Card roundedAbove="sm">
                  <div style={{ marginBottom: "16px" }}>
                    <Text as="h2" variant="headingSm">
                      {t("products")}
                    </Text>
                  </div>
                  <DataTable
                    collection={orderEntity.items || []}
                    columns={[
                      { key: "sku", name: t("sku") },
                      { key: "supplier_sku", name: t("supplier_sku") },
                      { key: "productTitle", name: t("product_name") },
                      {
                        key: "quantity",
                        isDisabledTotal: true,
                        type: "number",
                        name: t("total_quantity"),
                      },
                      {
                        key: "unitPrice",
                        type: "currency",
                        name: t("price"),
                        isDisabledTotal: true,
                      },
                      {
                        key: "finalPrice",
                        type: "currency",
                        name: t("total_amount"),
                      },
                    ]}
                  />
                </Card>
                {(orderEntity.productGroups || []).length > 0 && (
                  <Card roundedAbove="sm">
                    <div style={{ marginBottom: "16px" }}>
                      <Text as="h2" variant="headingSm">
                        {t("product_groups")}
                      </Text>
                    </div>
                    <DataTable
                      collection={(orderEntity.productGroups || []).map((pg: any) => ({
                        id: pg.id,
                        productTitle: pg.productGroup?.name || pg.productGroup?.id || pg.id,
                        code: pg.productGroup?.code || "",
                        quantity: pg.quantity || 0,
                        unitPrice: pg.unitPrice || 0,
                        finalPrice: pg.totalPrice || 0,
                      }))}
                      columns={[
                        { key: "code", name: t("code") },
                        { key: "productTitle", name: t("product_group") },
                        {
                          key: "quantity",
                          isDisabledTotal: true,
                          type: "number",
                          name: t("total_quantity"),
                        },
                        {
                          key: "unitPrice",
                          type: "currency",
                          name: t("price"),
                          isDisabledTotal: true,
                        },
                        {
                          key: "finalPrice",
                          type: "currency",
                          name: t("total_amount"),
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            )}

            {selected === "fulfillment" && orderId && companyId && (
              <FulfillmentSection orderId={orderId} companyId={companyId} />
            )}

            {selected === "amendments" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("amendments")}
                  </Text>
                  {canStartAmendment &&
                    <InlineStack align="center">
                      <Button
                        onClick={handleCreateAmendment}
                        disabled={isAmendmentCreateDisabled}
                      >
                        {t("files_build_pdf")}
                      </Button>
                    </InlineStack>
                  }
                </InlineGrid>
                {(orderEntity.amendmentFiles?.length ?? 0) > 0 ? (
                  <Files
                    files={orderEntity.amendmentFiles || []}
                    customerId={orderEntity.customer.id}
                    parentTemplateId={
                      companySettingsEntity?.orderAmendmentTemplateSource ===
                        "custom" && companySettingsEntity?.orderAmendmentTemplate
                        ? companySettingsEntity.orderAmendmentTemplate
                        // An external contract has no master agreement to fall
                        // back to — its amendments use the company's amendment
                        // slot instead (same guard as canStartAmendment /
                        // handleCreateAmendment above).
                        : isExternalOrder
                        ? undefined
                        : orderEntity.customer?.masterAgreementFileId?.templateId
                    }
                    orderEntity={orderEntity}
                    isOrderAmendment
                    onChanged={loadOrderData}
                  />
                ) : (
                  <EmptyState
                    heading={t("no_files")}
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <Button
                      onClick={handleCreateAmendment}
                      disabled={
                        !orderEntity?.files?.[0]?.isSigned ||
                        isAmendmentCreateDisabled ||
                        (isExternalOrder && !amendmentSlotReady)
                      }
                    >
                      {t("files_build_pdf")}
                    </Button>
                    {isExternalOrder && !amendmentSlotReady && (
                      <p className="mt-2 text-sm text-zinc-500">
                        {t("amendment_slot_required")}
                      </p>
                    )}
                  </EmptyState>
                )}
              </Card>
            )}

            {selected === "emails" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("emails")}
                  </Text>
                </InlineGrid>
                <Emails emails={orderEntity.emails} />
              </Card>
            )}
            
            <Card roundedAbove="sm">
              <CommentsFeature
                id={orderId ?? ""}
                CommentTypes={CommentTypes.Orders}
              />
            </Card>
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          {showResetButton && (
            <div style={{ display: "flex", height: "51px", alignItems: "center", justifyContent: "flex-end", marginBottom: "16px" }}>
              <Button
                tone="critical"
                variant="primary"
                onClick={handleResetSignaturesClick}
              >
                {t("remove_signed_document")}
              </Button>
            </div>
          )}
          {/* The top gap lines the steps up with the tab content beside them;
              where they stack under it (below 1100 px, as for OrderStatusStrip)
              it is just empty space. */}
          <div
            id={ORDER_STEPS_ID}
            className={showResetButton ? undefined : "min-[1100px]:mt-[67px]"}
          >
            <BlockStack gap="500">
              {/* box 1 */}
              <div className="card-wrapper">
                <div className="card-box border-green">
                  <Card roundedAbove="sm">
                    <Text as="h2" variant="headingSm">
                      {t("select_products")}
                    </Text>
                    <InlineStack align="center">{ProductEditNode}</InlineStack>
                  </Card>
                </div>
                <div className="connector-wrapper">
                  <div className="vertical-line"></div>
                  <div className="arrow-down"></div>
                </div>
              </div>
              {/* An external contract is not generated or signed here: one box
                  to attach its signed PDF stands in for boxes 2 and 3. */}
              {isExternalOrder && companyId && (
                <ExternalDocumentBox
                  companyId={companyId}
                  orderEntity={orderEntity}
                  downloadUrl={id ? downloadUrl : undefined}
                  canAttach={!!(hasUserSignaturePermission || isUserMaster)}
                  onChanged={loadOrderData}
                />
              )}
              {!isExternalOrder && (
              <>
              {/* box 2 */}
              <div className="card-wrapper">
                <div
                  className={`card-box ${
                    templateId
                      ? "border-green"
                      : !templateId
                      ? "border-yellow"
                      : "border-grey"
                  }`}
                >
                  <Card roundedAbove="sm">
                    <Text as="h2" variant="headingSm">
                      {t("generate_order_document")}
                    </Text>
                    {orderEntity.customer?.masterAgreementFileId?.isSigned &&
                      orderEntity?.files?.length < 1 && (
                        <InlineStack align="center">
                          <Button
                            onClick={() =>
                              handleDocumentClick(
                                orderEntity.customer?.id,
                                orderEntity.customer?.masterAgreementFileId
                                  ?.templateId,
                                orderEntity.customer?.masterAgreementFileId?.id,
                                defaultContactUserId,
                                orderEntity
                              )
                            }
                          >
                            {t("files_build_pdf")}
                          </Button>
                        </InlineStack>
                      )}
                    {!orderEntity.customer?.masterAgreementFileId?.isSigned && !orderEntity?.files?.length && (
                      <OrderContractHint
                        finance={orderEntity.customer?.finance}
                        agreementFile={orderEntity.customer?.masterAgreementFileId}
                        orderCreatedAt={orderEntity.createdAt}
                        companyId={companyId}
                        customerId={orderEntity.customer?.id}
                      />
                    )}

                    {orderEntity?.files?.length > 0 && (
                      <>
                        <InlineStack align="center">
                          <div style={{ marginBottom: "5px" }}>
                            <Link url={downloadUrl} target="_blank">
                              {orderEntity?.files[0].name ||
                                orderEntity?.files[0].title}
                            </Link>
                          </div>
                        </InlineStack>
                        <InlineStack align="center">
                          {TemplateEditNode}
                        </InlineStack>
                      </>
                    )}
                  </Card>
                </div>
                <div className="connector-wrapper">
                  <div className="vertical-line"></div>
                  <div className="arrow-down"></div>
                </div>
              </div>
              {/* box 3 */}
              <div className="card-wrapper">
                <div
                  className={`card-box ${
                    isSigned
                      ? "border-green"
                      : templateId
                      ? "border-yellow"
                      : "border-grey"
                  }`}
                >
                  <Card roundedAbove="sm">
                    <Text as="h2" variant="headingSm">
                      {t("sign_the_order")}
                    </Text>

                    <InlineStack gap="100" align="center">
                      {orderEntity?.customer?.id &&
                        contactSigners.length > 0 &&
                        templateId &&
                        !isSigned &&
                        signType === "Dokobit" &&
                        canMakeNewRequest &&
                        signingStepsRequired &&
                        signingStepsRequired === 1 && (
                          <div style={{ marginTop: "5px" }}>
                            <Button
                              onClick={() => {
                                handleUploadClick(
                                  id,
                                  contactSigners
                                    .map((signer: any) => getSignerId(signer))
                                    .filter((sid: string | null): sid is string => !!sid),
                                  getSignerId(permissionUserSigner) ?? "",
                                  orderEntity.customer?.id,
                                  getSignerId(userSigner) ?? "",
                                  signType
                                );
                              }}
                            >
                              {t("sign_request")}
                            </Button>
                          </div>
                        )}
                    </InlineStack>

                    <InlineStack gap="100" align="center">
                      {templateId &&
                        orderEntity.customer?.id &&
                        !isSigned &&
                        fileId &&
                        signingStepsRequired &&
                        signingStepsRequired === 2 &&
                        !dokobit?.fileToken &&
                        signType === "Dokobit" && (
                          <div style={{ marginTop: "5px" }}>
                            <Button
                              onClick={() => {
                                FileSignUserAssignHandler(
                                  id,
                                  relatedTo,
                                  getSignerId(permissionUserSigner) ?? "",
                                  getSignerId(userSigner) ?? "",
                                  orderEntity.customer?.id
                                );
                              }}
                            >
                              {t("sign_request")}
                            </Button>
                          </div>
                        )}
                    </InlineStack>
                    <InlineStack gap="100" align="center">
                      {orderEntity?.customer?.id &&
                        signType === "Manual" &&
                        !isFileSigned && (
                          <Button
                            onClick={() => {
                              handleSendEmailWithAttachment(
                                orderEntity?.files[0].id,
                                orderEntity?.customer?.id
                              );
                            }}
                          >
                            {t("send_email")}
                          </Button>
                        )}
                      {/* if want to upload manually signed document */}
                      {orderEntity?.customer?.id &&
                        signType === "Manual" &&
                        !isFileSigned && (
                          <ReplaceFile
                            title={t("upload_signed_document")}
                            onChange={() => ""}
                            subject="customer"
                            parentFileId={id}
                          />
                        )}
                    </InlineStack>

                    {(isDokobitDocument || isSignontabDocument) && customerContactSigned?.length > 0 && (
                      <div className="content_break content_margin">
                        {customerContactSigned.map((signer) => (
                          <InlineStack key={signer.id} gap="200" blockAlign="center" wrap>
                            <Text variant="bodyMd" as="p">
                              {t("sign_status")}: {signer.name}{" "}
                              {signer?.signed ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.signing_time}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="success">{t("signed")}</Badge>
                                </>
                              ) : (
                                <>
                                  <PrettyDate
                                    dateString={dokobit
                                      ? dokobit?.createdAt
                                      : signontab
                                        ? signontab?.createdAt
                                        : ''}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="attention">{t("pending")}</Badge>
                                </>
                              )}
                            </Text>
                            {!signer?.signed &&
                              signer?.signatureId &&
                              signer?.status !== "rejected" &&
                              isDokobitDocument &&
                              !isSigned && (
                                <Button
                                  size="slim"
                                  onClick={() =>
                                    handleRepeatEmail(signer.signatureId)
                                  }
                                >
                                  {t("repeat_email")}
                                </Button>
                              )}
                          </InlineStack>
                        ))}
                      </div>
                    )}
                    {(isDokobitDocument || isSignontabDocument) &&
                      usersSignerList?.length > 0 &&
                      (dokobit?.fileToken || signontab) && (
                        <div className="content_break content_margin">
                          {usersSignerList?.map((signer: any) => (
                            <InlineStack
                              key={signer.id}
                              gap="200"
                              blockAlign="center"
                              wrap
                            >
                              <Text variant="bodyMd" as="p">
                                {t("sign_status")}: {signer.name}{" "}
                                {signer.signed ? (
                                  <>
                                    <PrettyDate
                                      dateString={signer.signing_time}
                                      showTime={true}
                                    />{" "}
                                    <Badge tone="success">{t("signed")}</Badge>
                                  </>
                                ) : (
                                  <>
                                    <PrettyDate
                                      dateString={
                                        dokobit
                                          ? dokobit?.createdAt
                                          : signontab
                                            ? signontab?.createdAt
                                            : ""
                                      }
                                      showTime={true}
                                    />{" "}
                                    <Badge tone="attention">
                                      {t("pending")}
                                    </Badge>
                                  </>
                                )}
                              </Text>
                              {!signer.signed &&
                                signer.status !== "rejected" &&
                                signer.id === session?.id &&
                                isDokobitDocument &&
                                !isSigned && (
                                  <Button
                                    size="slim"
                                    onClick={() =>
                                      dokobitFileViewHandler(companyId!, id)
                                    }
                                  >
                                    {t("sign")}
                                  </Button>
                                )}
                            </InlineStack>
                          ))}
                        </div>
                      )}
                    {isSigned && (
                      <div style={{ marginTop: "8px" }}>
                        <Badge tone="success">{t("signed")}</Badge>
                      </div>
                    )}
                    {!isSigned && usersSignerList?.length > 0 && (
                      <InlineStack align="center">
                        <div style={{ marginTop: "10px" }}>
                          {TemplateEditNode}
                        </div>
                      </InlineStack>
                    )}
                  </Card>
                </div>
                <div className="connector-wrapper">
                  <div className="vertical-line"></div>
                  <div className="arrow-down"></div>
                </div>
              </div>
              </>
              )}
              {/* box 4 — invoices of the order: the latest one and what happens next */}
              <div
                className={`card-box ${
                  invoiceCount > 0
                    ? "border-green"
                    : hasProducts
                    ? "border-yellow"
                    : "border-grey"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("invoice_customer")}
                  </Text>
                  <div className="mt-2 space-y-2">
                    {lastInvoice && (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span>
                          {t("invoice_last", { defaultValue: "Last" })}:{" "}
                          {lastInvoiceNumber && `${lastInvoiceNumber}, `}
                          <Currency value={lastInvoice._amountWithVat} isSymbol />
                        </span>
                        <InvoiceStatusBadge invoice={lastInvoice} />
                      </div>
                    )}
                    {showContractDeferred && companyId && orderId && (
                      <ContractDeferredSwitch
                        companyId={companyId}
                        orderId={orderId}
                        contractDeferred={!!orderEntity.contractDeferred}
                        canChange={!!isUserMaster}
                        onChanged={loadOrderData}
                      />
                    )}
                    <InvoiceHints hints={invoiceHints} />
                    {/* Before the first invoice this step is where it is made, so the
                        action stays here (unless the invoices tab, which carries the
                        same button, is already open); afterwards the list lives in
                        its tab. */}
                    {invoiceCount > 0 ? (
                      <button
                        type="button"
                        className={btnLinkCls}
                        onClick={() => handleTabChange("invoices")}
                      >
                        {t("invoices_all", { defaultValue: "All invoices" })} →
                      </button>
                    ) : selected !== "invoices" && (
                      <button
                        type="button"
                        className={btnPrimaryCls}
                        disabled={!canCreateInvoice}
                        onClick={handleGenerateInvoice}
                      >
                        {t("create_invoice")}
                      </button>
                    )}
                  </div>
                </Card>
              </div>
            </BlockStack>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default OrderContainer;
